import React from "react";
import { useNavigate } from "react-router-dom";

function EditPlanningTable({ rows = [], onEditRow }) {
  const navigate = useNavigate();

  if (!Array.isArray(rows) || rows.length === 0) {
    return (
      <div className="alert alert-info mt-3 mb-0">
        No planning rows found for the selected filters.
      </div>
    );
  }

  // ---------- helpers ----------
  const badge = (text, variant = "secondary", extra = "") => (
    <span className={`badge bg-${variant} ${extra}`.trim()}>{text}</span>
  );

  const isDateish = (val) => {
    if (!val) return false;
    if (val instanceof Date && !isNaN(val)) return true;
    if (typeof val === "string") {
      const s = val.trim();
      // accepts 'YYYY-MM-DD' or 'YYYY-MM-DD HH:mm:ss' or ISO 'YYYY-MM-DDTHH:mm:ss'
      return /^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/.test(s);
    }
    return false;
  };

  const num = (v) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : 0;
  };

  const fmtQty = (v) =>
    v !== null && v !== undefined && !isNaN(Number(v)) ? Number(v).toFixed(3) : "-";

  const packingCell = (r) => r.packing_style ?? r.packing_type ?? "-";

  // ✅ Determine if logistic is "Done"
  const isLogisticDone = (r) => {
    const txt = (r?.logistic_status ?? "").toString().trim().toLowerCase();
    if (txt === "done") return true;
    if (txt === "pending") return false;

    const vehiclesPlanned = num(r?.vehicles_planned);
    const logisticPlannedQty = num(r?.logistic_planned_qty);
    const hasVehiclesArray = Array.isArray(r?.vehicles) && r.vehicles.length > 0;

    let hasPlanDate =
      isDateish(r?.logistic_planned_date) ||
      isDateish(r?.logistics_planned_date) ||
      isDateish(r?.logistic_planned_on) ||
      isDateish(r?.logistics_planned_on) ||
      isDateish(r?.vehicle_planned_date) ||
      isDateish(r?.vehicle_planned_on) ||
      isDateish(r?.dispatch_date);

    if (!hasPlanDate && Array.isArray(r?.vehicles)) {
      hasPlanDate = r.vehicles.some(
        (v) =>
          isDateish(v?.dispatch_date) ||
          isDateish(v?.planned_on) ||
          isDateish(v?.vehicle_planned_on)
      );
    }

    return vehiclesPlanned > 0 || logisticPlannedQty > 0 || hasVehiclesArray || hasPlanDate;
  };

  const renderLogisticStatus = (r) =>
    isLogisticDone(r) ? badge("Done", "success") : badge("Pending", "danger");

  // 👉 navigate to detail page with id + pass the whole row in state
  const goToEdit = (row) => {
    const id = row?.planning_id ?? row?.plan_id ?? row?.id;
    if (!id) {
      alert("Planning ID not found on this row.");
      return;
    }
    navigate(`/planning/plan/${id}`, { state: { plan: row } });
  };

  return (
    <div className="table-responsive mt-3">
      <table className="table table-sm table-bordered align-middle text-wrap">
        <thead className="table-light">
          <tr>
            <th>#</th>
            <th>Customer</th>
            <th>Material</th>
            <th>Packing</th>
            <th className="text-end">Planned Qty</th>
            <th>Delivery</th>
            <th>Destination</th>
            <th>Priority</th>
            <th>Logistic</th>
            <th className="text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const logisticDone = isLogisticDone(r);
            return (
              <tr key={r.id ?? r.plan_id ?? `${r.sales_order_id || "row"}_${i}`}>
                <td>{i + 1}</td>
                <td>{r.customer_name ?? r.customer ?? "-"}</td>
                <td>{r.material_name ?? "-"}</td>
                <td>{packingCell(r)}</td>
                <td className="text-end">{fmtQty(r.planned_qty)}</td>
                <td>{r.delivery_date ?? "-"}</td>
                <td>{r.destination_point ?? r.destination ?? "-"}</td>
                <td>{r.priority ?? "-"}</td>
                <td>{renderLogisticStatus(r)}</td>
                <td className="text-center">
                  {!logisticDone && (
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => goToEdit(r)}
                    >
                      ✏️ Edit
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default EditPlanningTable;
